/**
 * ForceEditPanel - Force editing component with its own Refrakt store
 *
 * Props:
 * - slug: Slug of force to edit (loads or creates draft when changed)
 * - projectId: Current project ID
 * - accessToken: Auth token for API calls
 *
 * Events:
 * - save-success: Save completed (detail: Slug)
 * - delete-success: Delete completed (detail: Slug)
 */
import {
  css,
  html,
  LitElement,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../../styles.ts";
import * as ForceCategory from "../../../../models/force-category.ts";
import * as PaceLayer from "../../../../models/pace-layer.ts";
import * as Arc from "../../../../models/arc.ts";
import type { SelectOption } from "../../dui/select/dui-select.ts";
import {
  type Action,
  createForceEditStore,
  type ForceEditStore,
  type Model,
  model,
} from "../../force-edit-store.ts";

// Components
import "../../dui/select/dui-select.ts";
import "../../dui/field/dui-field.ts";
import "../../dui/field/dui-field-label.ts";
import "../../dui/field/dui-field-description.ts";
import "../../dui/input/dui-input.ts";
import "../../dui/textarea/dui-textarea.ts";
import "../../dui/slider/dui-slider.ts";
import "../../dui/vstack/dui-vstack.ts";
import "../../dui/hstack/dui-hstack.ts";
import "../category-icon.ts";
import "../../dui/button/dui-button.ts";
import "../loading.ts";
import "../../dui/alert-dialog/dui-alert-dialog.ts";
import "../../dui/alert-dialog/dui-alert-dialog-popup.ts";
import "../../dui/alert-dialog/dui-alert-dialog-title.ts";
import "../../dui/alert-dialog/dui-alert-dialog-description.ts";
import "../../dui/alert-dialog/dui-alert-dialog-close.ts";
import "./canvas-panel.ts";

export const arcOptions = (): SelectOption[] => {
  const grow: Arc.Arc = "grow";
  const collapse: Arc.Arc = "collapse";
  const hold: Arc.Arc = "hold";
  const transform: Arc.Arc = "transform";

  return [
    { label: Arc.toDesc(grow), value: grow },
    { label: Arc.toDesc(collapse), value: collapse },
    { label: Arc.toDesc(hold), value: hold },
    { label: Arc.toDesc(transform), value: transform },
  ];
};

export const categoryOptions = (): SelectOption[] => {
  const social: ForceCategory.ForceCategory = "social";
  const technological: ForceCategory.ForceCategory = "technological";
  const economic: ForceCategory.ForceCategory = "economic";
  const environmental: ForceCategory.ForceCategory = "environmental";
  const political: ForceCategory.ForceCategory = "political";

  return [
    { label: ForceCategory.toDesc(social), value: social },
    { label: ForceCategory.toDesc(technological), value: technological },
    { label: ForceCategory.toDesc(economic), value: economic },
    { label: ForceCategory.toDesc(environmental), value: environmental },
    { label: ForceCategory.toDesc(political), value: political },
  ];
};

export const paceLayerOptions = (): SelectOption[] => {
  const months: PaceLayer.PaceLayer = "months";
  const years: PaceLayer.PaceLayer = "years";
  const decades: PaceLayer.PaceLayer = "decades";
  const centuries: PaceLayer.PaceLayer = "centuries";
  const millennia: PaceLayer.PaceLayer = "millennia";
  const eons: PaceLayer.PaceLayer = "eons";

  return [
    { label: PaceLayer.toDesc(months), value: months },
    { label: PaceLayer.toDesc(years), value: years },
    { label: PaceLayer.toDesc(decades), value: decades },
    { label: PaceLayer.toDesc(centuries), value: centuries },
    { label: PaceLayer.toDesc(millennia), value: millennia },
    { label: PaceLayer.toDesc(eons), value: eons },
  ];
};

const style = css`
  :host {
    display: block;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
  }

  .title-textfield {
    --textfield-font-size: var(--font-size-2xl);
    --textfield-line-height: var(--line-height-tight);
  }
`;

const ForceEditFormView = (
  state: Model,
  send: (action: Action) => void,
): TemplateResult => {
  const category = state.category;

  const onTitleInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-title",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onDescriptionInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-description",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onClickConfirmDelete = () => {
    send({ type: "toggle-delete-alert", show: true });
  };

  const onDeleteAlertOpenChange = (event: CustomEvent<{ open: boolean }>) => {
    if (!event.detail.open) {
      send({ type: "toggle-delete-alert", show: false });
    }
  };

  const onClickDelete = () => {
    send({
      type: "request-delete",
      slug: state.slug,
      projectId: state.projectId,
    });
  };

  const onCategorySelect = (
    event: CustomEvent<{ value: string; option: SelectOption }>,
  ) => {
    send({
      type: "set-category",
      value: ForceCategory.into(event.detail.value),
      accessToken: state.accessToken,
    });
  };

  const onArcSelect = (
    event: CustomEvent<{ value: string; option: SelectOption }>,
  ) => {
    send({
      type: "set-arc",
      value: event.detail.value as Arc.Arc | undefined,
      accessToken: state.accessToken,
    });
  };

  const onPaceLayerSelect = (
    event: CustomEvent<{ value: string; option: SelectOption }>,
  ) => {
    send({
      type: "set-pace-layer",
      value: PaceLayer.into(event.detail.value),
      accessToken: state.accessToken,
    });
  };

  const onImpactRangeChange = (event: CustomEvent<{ value: number }>) => {
    send({
      type: "set-impact",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onCertaintyRangeChange = (event: CustomEvent<{ value: number }>) => {
    send({
      type: "set-certainty",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  return html`
    <dui-vstack>
      <dui-hstack>
        <category-icon .category="${category}"> </category-icon>
        <div>${ForceCategory.toDesc(category)}</div>
      </dui-hstack>
      <dui-field>
        <dui-field-label>Title</dui-field-label>
        <dui-input
          class="title-textfield"
          @input-change="${onTitleInput}"
          .value="${state.title}"
          placeholder="Untitled"
        ></dui-input>
      </dui-field>
      <dui-field>
        <dui-field-label>Description</dui-field-label>
        <dui-textarea
          class="description-textfield"
          rows="20"
          @textarea-change="${onDescriptionInput}"
          .value="${state.description}"
          placeholder="..."
        ></dui-textarea>
      </dui-field>
      <dui-field>
        <dui-field-label>Impact</dui-field-label>
        <dui-slider
          .value="${state.impact}"
          .min="${0}"
          .max="${1}"
          .step="${0.01}"
          @value-change="${onImpactRangeChange}"
        ></dui-slider>
        <dui-field-description>
          What is the scale of impact (local - global)?
        </dui-field-description>
      </dui-field>
      <dui-field>
        <dui-field-label>Certainty</dui-field-label>
        <dui-slider
          .value="${state.certainty}"
          .min="${0}"
          .max="${1}"
          .step="${0.01}"
          @value-change="${onCertaintyRangeChange}"
        ></dui-slider>
        <dui-field-description>
          How certain is this force (uncertain - certain)?
        </dui-field-description>
      </dui-field>
      <dui-field>
        <dui-field-label>Category</dui-field-label>
        <dui-select
          .options="${categoryOptions()}"
          .value="${state.category ?? ""}"
          @value-change="${onCategorySelect}"
        ></dui-select>
        <dui-field-description>
          What category does this force primarily belong to?
        </dui-field-description>
      </dui-field>
      <dui-field>
        <dui-field-label>Arc</dui-field-label>
        <dui-select
          .options="${arcOptions()}"
          .value="${state.arc ?? ""}"
          @value-change="${onArcSelect}"
        ></dui-select>
        <dui-field-description>
          What is the directionality of the force? Is it a positive feedback
          loop (grow)? Is it a downward trajectory (collapse)? Does it freeze or
          maintain the status quo (hold)? Is it a complete transformation of the
          game, its rules, and goals (transform)?
        </dui-field-description>
      </dui-field>
      <dui-field>
        <dui-field-label>Pace Layer</dui-field-label>
        <dui-select
          .options="${paceLayerOptions()}"
          .value="${state.paceLayer ?? ""}"
          @value-change="${onPaceLayerSelect}"
        ></dui-select>
        <dui-field-description>
          Over what time period will this force exert its influence?
        </dui-field-description>
      </dui-field>

      <hr />
      <h2>Danger zone</h2>
      <dui-button variant="destructive" @click="${onClickConfirmDelete}"
        >Delete</dui-button
      >
    </dui-vstack>

    <dui-alert-dialog
      slot="overlay"
      .open="${state.isConfirmDeleteMenuOpen}"
      @alert-dialog-open-change="${onDeleteAlertOpenChange}"
    >
      <dui-alert-dialog-popup>
        <dui-alert-dialog-title>Delete force</dui-alert-dialog-title>
        <dui-alert-dialog-description>
          Are you sure you want to delete this&nbsp;force?
        </dui-alert-dialog-description>
        <dui-button variant="destructive" @click="${onClickDelete}"
          >Delete</dui-button
        >
        <dui-alert-dialog-close
          ><dui-button variant="outline"
            >Cancel</dui-button
          ></dui-alert-dialog-close
        >
      </dui-alert-dialog-popup>
    </dui-alert-dialog>
  `;
};

const ForceEditLoadingView = () => {
  return html` <loading-view size="md"></loading-view> `;
};

@customElement("force-edit-panel")
export class ForceEditPanelElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, style];

  /** Slug of force to edit */
  @property({ type: String })
  accessor slug: string | undefined = undefined;

  /** Current project ID */
  @property({ type: String })
  accessor projectId: string | undefined = undefined;

  /** Auth token for API calls */
  @property({ type: String })
  accessor accessToken: string | undefined = undefined;

  #store: ForceEditStore = createForceEditStore(model(), this);

  override willUpdate(changedProperties: PropertyValues<this>): void {
    // When accessToken changes, update the store
    if (changedProperties.has("accessToken")) {
      this.#store.send({
        type: "set-access-token",
        accessToken: this.accessToken,
      });
    }

    // When slug/projectId change, load the force (or create draft if not found)
    if (
      (changedProperties.has("slug") || changedProperties.has("projectId")) &&
      this.slug &&
      this.projectId
    ) {
      this.#store.send({
        type: "load-force",
        slug: this.slug,
        projectId: this.projectId,
        accessToken: this.accessToken,
      });
    }
  }

  override render() {
    const state = this.#store.get();

    return html`
      <canvas-panel inset>
        <div slot="title">Edit force</div>
        ${state.slug
          ? ForceEditFormView(state, this.#store.send)
          : ForceEditLoadingView()}
      </canvas-panel>
    `;
  }
}
